import {
  Flex,
  Heading,
  Input,
  Button,
  InputGroup,
  Stack,
  InputLeftElement,
  Box,
  Avatar,
  FormControl,
  FormLabel,
  Text,
  useToast,
  VStack,
  Container,
  InputRightElement,
  FormErrorMessage,
  RadioGroup,
  Radio,
  Badge,
  Grid,
  GridItem,
  HStack
} from "@chakra-ui/react";

import { PhoneIcon, EmailIcon, LockIcon, ViewIcon, ViewOffIcon } from '@chakra-ui/icons';
import { useState } from "react";
import { register } from '../apis/registerApi';
import { FaUserAlt, FaGolfBall } from "react-icons/fa";
import { useNavigate } from 'react-router-dom';

const Register = () => {
  const [formValues, setFormValues] = useState({
    firstname: '',
    lastname: '',
    phone: '',
    email: '',
    password: '',
    membershipType: 'basic'
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const toast = useToast();
  const navigate = useNavigate();

  const handleInputChange = (event) => {
    let inputValue = event.target.value;
    const name = event.target.name;

    if (name === "phone") {
      // Remove non-digit characters from the input
      inputValue = inputValue.replace(/\D/g, '');

      // Check if the length is more than 10
      if (inputValue.length > 10) {
        return; // Don't update the state if the length is more than 10
      }

      // Apply the phone number format
      inputValue = inputValue.replace(/(\d{3})(\d{0,3})(\d{0,4})/, (_, p1, p2, p3) => {
        let formattedNumber = p1;
        if (p2) formattedNumber += `-${p2}`;
        if (p3) formattedNumber += `-${p3}`;
        return formattedNumber;
      });
    }

    // Clear error when user types
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }

    setFormValues({
      ...formValues,
      [name]: inputValue
    });
  };

  const handleMembershipChange = (value) => {
    setFormValues({
      ...formValues,
      membershipType: value
    });
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Validate first name
    if (!formValues.firstname.trim()) {
      newErrors.firstname = 'First name is required';
    }
    
    // Validate last name
    if (!formValues.lastname.trim()) {
      newErrors.lastname = 'Last name is required';
    }
    
    // Validate phone
    if (!formValues.phone) {
      newErrors.phone = 'Phone number is required';
    } else if (formValues.phone.replace(/\D/g, '').length !== 10) {
      newErrors.phone = 'Phone number must be 10 digits';
    }
    
    // Validate email
    if (!formValues.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formValues.email)) {
      newErrors.email = 'Email address is invalid';
    }
    
    // Validate password
    if (!formValues.password) {
      newErrors.password = 'Password is required';
    } else if (formValues.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    
    // Validate membership type
    if (!formValues.membershipType) {
      newErrors.membershipType = 'Please select a membership type';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const registerSubmit = async (e) => {
    let membershipstatus;
    
    e.preventDefault();
    
    if (!validateForm()) {
      toast({
        title: "Form validation error",
        description: "Please check if all fields are filled and try again",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const response = await register(
        formValues.firstname, 
        formValues.lastname, 
        formValues.phone, 
        formValues.email, 
        formValues.password,
        formValues.membershipType
      );
      
      toast({
        title: "Registration successful",
        description: "Please verify your account with the OTP code sent to your phone",
        status: "success",
        duration: 1000,
        isClosable: true,
        position: "top",
      });
      
      // For both membership types, go to OTP verification first
      // But pass additional data for unlimited membership
      navigate(response.redirectPath, { 
        state: { 
          email: formValues.email,
          membershipType: formValues.membershipType,
          name: `${formValues.firstname} ${formValues.lastname}`,
          requiresPayment: formValues.membershipType === 'unlimited',
          amount: formValues.membershipType === 'unlimited' ? 250.00 : 0
        } 
      });
    } catch (error) {
      toast({
        title: "Registration failed",
        description: error.message || "There was an error creating your account. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Flex
      minH="100vh"
      align="center"
      justify="center"
      p={{ base: 1, md: 2 }}
    >
      <Container maxW={{ base: "100%", md: "container.md" }} p={0}>
        <Box
          bg="white"
          p={{ base: 3, md: 5 }}
          borderRadius="lg"
        >
          <HStack spacing={4} mb={3} align="center">
            <Avatar bg="#82ca9c" size={{ base: "sm", md: "md" }} />
            <Box>
              <Heading as="h1" size={{ base: "md", md: "lg" }} color="#82ca9c">
                Sign Up
              </Heading>
              <Text color="gray.600" fontSize={{ base: "xs", md: "sm" }}>
                Create your account to get started
              </Text>
            </Box>
          </HStack>

          <form onSubmit={registerSubmit}>
            <Grid 
              templateColumns={{ base: "repeat(1, 1fr)", md: "repeat(2, 1fr)" }} 
              gap={{ base: 2, md: 4 }}
            >
              <GridItem>
                <FormControl isInvalid={errors.firstname} size="sm">
                  <FormLabel fontSize="xs" fontWeight="medium" color="gray.700" mb={1}>First Name</FormLabel>
                  <InputGroup size="sm">
                    <InputLeftElement
                      pointerEvents="none"
                      children={<FaUserAlt color="#CBD5E0" />}
                    />
                    <Input
                      type="text"
                      name="firstname"
                      value={formValues.firstname}
                      onChange={handleInputChange}
                      placeholder="Enter your first name"
                      borderRadius="md"
                      _focus={{ borderColor: "#82ca9c", boxShadow: "0 0 0 1px #82ca9c" }}
                    />
                  </InputGroup>
                  <FormErrorMessage fontSize="2xs">{errors.firstname}</FormErrorMessage>
                </FormControl>
              </GridItem>

              <GridItem>
                <FormControl isInvalid={errors.lastname} size="sm">
                  <FormLabel fontSize="xs" fontWeight="medium" color="gray.700" mb={1}>Last Name</FormLabel>
                  <InputGroup size="sm">
                    <InputLeftElement
                      pointerEvents="none"
                      children={<FaUserAlt color="#CBD5E0" />}
                    />
                    <Input
                      type="text"
                      name="lastname"
                      value={formValues.lastname}
                      onChange={handleInputChange}
                      placeholder="Enter your last name"
                      borderRadius="md"
                      _focus={{ borderColor: "#82ca9c", boxShadow: "0 0 0 1px #82ca9c" }}
                    />
                  </InputGroup>
                  <FormErrorMessage fontSize="2xs">{errors.lastname}</FormErrorMessage>
                </FormControl>
              </GridItem>

              <GridItem>
                <FormControl isInvalid={errors.phone} size="sm">
                  <FormLabel fontSize="xs" fontWeight="medium" color="gray.700" mb={1}>Phone Number</FormLabel>
                  <InputGroup size="sm">
                    <InputLeftElement
                      pointerEvents="none"
                      children={<PhoneIcon color="#CBD5E0" />}
                    />
                    <Input
                      type="tel"
                      name="phone"
                      placeholder="123-456-7890"
                      value={formValues.phone}
                      onChange={handleInputChange}
                      borderRadius="md"
                      _focus={{ borderColor: "#82ca9c", boxShadow: "0 0 0 1px #82ca9c" }}
                    />
                  </InputGroup>
                  <FormErrorMessage fontSize="2xs">{errors.phone}</FormErrorMessage>
                </FormControl>
              </GridItem>

              <GridItem>
                <FormControl isInvalid={errors.email} size="sm">
                  <FormLabel fontSize="xs" fontWeight="medium" color="gray.700" mb={1}>Email Address</FormLabel>
                  <InputGroup size="sm">
                    <InputLeftElement
                      pointerEvents="none"
                      children={<EmailIcon color="#CBD5E0" />}
                    />
                    <Input
                      type="email"
                      name="email"
                      value={formValues.email}
                      onChange={handleInputChange}
                      placeholder="Enter your email"
                      borderRadius="md"
                      _focus={{ borderColor: "#82ca9c", boxShadow: "0 0 0 1px #82ca9c" }}
                    />
                  </InputGroup>
                  <FormErrorMessage fontSize="2xs">{errors.email}</FormErrorMessage>
                </FormControl>
              </GridItem>

              <GridItem colSpan={{ base: 1, md: 2 }}>
                <FormControl isInvalid={errors.password} size="sm">
                  <FormLabel fontSize="xs" fontWeight="medium" color="gray.700" mb={1}>Password</FormLabel>
                  <InputGroup size="sm">
                    <InputLeftElement
                      pointerEvents="none"
                      children={<LockIcon color="#CBD5E0" />}
                    />
                    <Input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      placeholder="Create a password"
                      value={formValues.password}
                      onChange={handleInputChange}
                      borderRadius="md"
                      _focus={{ borderColor: "#82ca9c", boxShadow: "0 0 0 1px #82ca9c" }}
                    />
                    <InputRightElement width="2.5rem">
                      <Button 
                        h="1.25rem" 
                        size="xs" 
                        onClick={togglePasswordVisibility}
                        bg="transparent"
                        _hover={{ bg: "transparent" }}
                      >
                        {showPassword ? <ViewOffIcon color="gray.500" /> : <ViewIcon color="gray.500" />}
                      </Button>
                    </InputRightElement>
                  </InputGroup>
                  <FormErrorMessage fontSize="2xs">{errors.password}</FormErrorMessage>
                </FormControl>
              </GridItem>

              <GridItem colSpan={{ base: 1, md: 2 }}>
                <FormControl isInvalid={errors.membershipType} size="sm">
                  <FormLabel fontSize="xs" fontWeight="medium" color="gray.700" mb={1}>Membership Type</FormLabel>
                  <RadioGroup 
                    onChange={handleMembershipChange} 
                    value={formValues.membershipType}
                    colorScheme="green"
                  >
                    <Stack direction={{ base: "column", md: "row" }} spacing={2}>
                      <Box 
                        as="label"
                        borderWidth="1px"
                        borderRadius="md"
                        borderColor={formValues.membershipType === 'basic' ? "#82ca9c" : "gray.200"}
                        bg={formValues.membershipType === 'basic' ? "rgba(130, 202, 156, 0.05)" : "white"}
                        px={3}
                        py={2}
                        cursor="pointer"
                        transition="all 0.2s"
                        flex="1"
                        _hover={{
                          borderColor: "#82ca9c",
                          boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)"
                        }}
                      >
                        <Flex align="center">
                          <Radio 
                            value="basic" 
                            mr={2}
                            colorScheme="green"
                            size="sm"
                          />
                          <Box flex="1">
                            <Flex align="center" flexWrap="wrap">
                              <Text fontWeight="medium" fontSize="xs" mr={1}>Basic Membership</Text>
                              <Badge colorScheme="green" fontSize="2xs" px={1} py={0} borderRadius="full">
                                Popular
                              </Badge>
                            </Flex>
                            <Text fontSize="2xs" color="gray.600">
                              Pay-as-you-go access
                            </Text>
                          </Box>
                          <Text fontWeight="bold" fontSize="sm" color="#82ca9c">
                            $0
                          </Text>
                        </Flex>
                      </Box>
                      
                      <Box 
                        as="label"
                        borderWidth="1px"
                        borderRadius="md"
                        borderColor={formValues.membershipType === 'unlimited' ? "#82ca9c" : "gray.200"}
                        bg={formValues.membershipType === 'unlimited' ? "rgba(130, 202, 156, 0.05)" : "white"}
                        px={3}
                        py={2}
                        cursor="pointer"
                        transition="all 0.2s"
                        flex="1"
                        _hover={{
                          borderColor: "#82ca9c",
                          boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)"
                        }}
                      >
                        <Flex align="center">
                          <Radio 
                            value="unlimited" 
                            mr={2}
                            colorScheme="green"
                            size="sm"
                          />
                          <Box flex="1">
                            <Flex align="center" flexWrap="wrap">
                              <Text fontWeight="medium" fontSize="xs" mr={1}>Unlimited Membership</Text>
                              <Badge colorScheme="purple" fontSize="2xs" px={1} py={0} borderRadius="full">
                                Best Value
                              </Badge>
                            </Flex>
                            <Text fontSize="2xs" color="gray.600">
                              Unlimited access
                            </Text>
                          </Box>
                          <Text fontWeight="bold" fontSize="sm" color="#82ca9c">
                            $250.00
                          </Text>
                        </Flex>
                      </Box>
                    </Stack>
                  </RadioGroup>
                  <FormErrorMessage fontSize="2xs">{errors.membershipType}</FormErrorMessage>
                </FormControl>
              </GridItem>

              <GridItem colSpan={{ base: 1, md: 2 }}>
                <Button
                  type="submit"
                  w="full"
                  mt={2}
                  py={4}
                  bg="#82ca9c"
                  color="white"
                  fontWeight="bold"
                  fontSize="sm"
                  borderRadius="md"
                  _hover={{ bg: "#6db889" }}
                  _active={{ bg: "#5ba97c" }}
                  boxShadow="0 2px 4px rgba(130, 202, 156, 0.3)"
                  isLoading={isSubmitting}
                  loadingText="Registering"
                  size="sm"
                >
                  Create Account
                </Button>
              </GridItem>
              
              <GridItem colSpan={{ base: 1, md: 2 }} textAlign="center">
                <Text fontSize="xs" color="gray.600" mt={1}>
                  Already have an account?{" "}
                  <Text 
                    as="span" 
                    color="#82ca9c" 
                    fontWeight="medium" 
                    cursor="pointer"
                    _hover={{ textDecoration: "underline" }}
                    onClick={() => navigate("/auth")}
                  >
                    Sign in
                  </Text>
                </Text>
              </GridItem>
            </Grid>
          </form>
        </Box>
      </Container>
    </Flex>
  );
};

export default Register;